import React, { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Icon, Button, Popover, Badge, Select } from "antd";
import moment from "moment";

import { ModuleHeader, ModuleHeaderWrapper } from "~/components/ModuleHeader";
import { toBRL } from "~/components/UI/InputCurrency";
import MonthPicker from "~/_shared/components/MonthPicker";
import Search from "~/_shared/components/Search";
import FilterPillsBar from "~/_shared/components/FilterPillsBar";
import RestoredFiltersNotice from "~/_shared/components/RestoredFiltersNotice";
import usePersistedFilters from "~/_shared/hooks/usePersistedFilters";

import * as extratoActions from "./actions";
import TableList from "./Components/TableList";
import {
    EXTRATO_FILTERS_KEY,
    getDefaultExtratoFilters,
    getExtratoRequest,
    isExtratoPeriodDefault,
    getExtratoPeriodLabel,
    countActiveExtratoFilters,
    filterExtratoRows,
} from "./helpers";

import styled from "styled-components";

var SummaryCard = styled.div`
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 12px 15px;
    text-align: center;
    flex: 1;
    min-width: 0;
`;

var ExtratoList = function() {
    var dispatch = useDispatch();
    var data = useSelector(function(state) { return state.extrato.data || []; });
    var summary = useSelector(function(state) { return state.extrato.summary || {}; });
    var filter = useSelector(function(state) { return state.extrato.filter || {}; });
    var isLoading = useSelector(function(state) { return state.extrato.isLoading; });
    var bancosData = useSelector(function(state) { return state.bancos && state.bancos.data || []; });

    // Filters kept across refreshes for a few minutes (per tenant/user/page).
    var persisted = usePersistedFilters(EXTRATO_FILTERS_KEY, getDefaultExtratoFilters);
    var applied = persisted.filters;
    var setApplied = persisted.setFilters;
    var defaults = persisted.defaults;

    var selectedMonth = applied.month;
    var customRange = applied.customRange;
    var selectedBankId = applied.bankId;
    var search = applied.search || "";
    var typeFilter = applied.type;

    var customFrom = customRange ? customRange.from : null;
    var customTo = customRange ? customRange.to : null;

    // Loads the restored (or default) period on mount, and again whenever period/bank change.
    useEffect(function() {
        dispatch(extratoActions.loadExtrato(getExtratoRequest(applied)));
    }, [selectedMonth, customFrom, customTo, selectedBankId, dispatch]);

    var handleMonthChange = function(value) {
        setApplied({ month: value, customRange: null });
    };

    var handleResetPeriod = function() {
        setApplied({ month: defaults.month, customRange: null });
    };

    var filtered = useMemo(function() {
        return filterExtratoRows(data, typeFilter, search);
    }, [data, typeFilter, search]);

    var isPeriodDefault = isExtratoPeriodDefault(applied, defaults);
    var periodLabel = getExtratoPeriodLabel(applied);

    var pills = [{ key: 'month', label: periodLabel, color: isPeriodDefault ? '#1890ff' : '#722ed1', icon: 'calendar', onRemove: isPeriodDefault ? undefined : handleResetPeriod }];
    if (selectedBankId) {
        var bank = bancosData.filter(function(b) { return b.id === selectedBankId; })[0];
        pills.push({ key: 'bank', label: bank ? bank.name : 'Banco', color: '#13c2c2', icon: 'bank', onRemove: function() { setApplied({ bankId: null }); } });
    }
    if (typeFilter !== 'all') pills.push({ key: 'type', label: typeFilter === 'credit' ? 'Receitas' : 'Despesas', color: typeFilter === 'credit' ? '#52c41a' : '#f5222d', onRemove: function() { setApplied({ type: 'all' }); } });
    if (search) pills.push({ key: 'search', label: 'Busca: ' + search, color: '#722ed1', icon: 'search', onRemove: function() { setApplied({ search: '' }); } });

    var restoredSummary = pills
        .filter(function(pill) { return pill.key !== 'month' || !isPeriodDefault; })
        .map(function(pill) { return pill.label; })
        .join(' · ');

    return (
        <div>
            <ModuleHeaderWrapper>
                <ModuleHeader
                    breadcrumbs={["Financeiro", "Extrato de Movimentações"]}
                    title="Extrato de Movimentações"
                    search={<Search value={search} placeholder="Pesquisar lançamentos..." onChange={function(e) { setApplied({ search: e.target.value }); }} />}
                    center={
                        <Popover trigger="click" placement="bottomRight" content={
                            <div style={{ width: 380 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                                    <span style={{ fontSize: 15, fontWeight: 600 }}>Filtros</span>
                                    {!persisted.isDefault && (
                                        <a style={{ fontSize: 12, color: '#f5222d' }} onClick={persisted.resetFilters}>
                                            Limpar tudo
                                        </a>
                                    )}
                                </div>
                                <div style={{ marginBottom: 18 }}>
                                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Período</div>
                                    <MonthPicker value={selectedMonth} onChange={handleMonthChange}
                                        onCustomRange={function(from, to) { setApplied({ customRange: { from: from, to: to } }); }}
                                        customRangeLabel={customRange ? moment(customRange.from).format('DD/MM/YY') + ' - ' + moment(customRange.to).format('DD/MM/YY') : null}
                                        showTodayButton={false} style={{ display: 'block', width: '100%' }} />
                                </div>
                                <div style={{ marginBottom: 18 }}>
                                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Conta bancária</div>
                                    <Select value={selectedBankId || undefined} placeholder="Selecione para calcular saldo" allowClear
                                        style={{ width: '100%' }} onChange={function(v) { setApplied({ bankId: v || null }); }}>
                                        {bancosData.map(function(b) { return <Select.Option key={b.id} value={b.id}>{b.name}</Select.Option>; })}
                                    </Select>
                                </div>
                                <div>
                                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tipo</div>
                                    <div style={{ display: 'flex', gap: 6 }}>
                                        {[
                                            { value: 'all', label: 'Todos', color: '#8c8c8c', icon: 'unordered-list' },
                                            { value: 'credit', label: 'Receitas', color: '#52c41a', icon: 'arrow-down' },
                                            { value: 'debit', label: 'Despesas', color: '#f5222d', icon: 'arrow-up' },
                                        ].map(function(opt) {
                                            var isActive = typeFilter === opt.value;
                                            return (
                                                <div key={opt.value} onClick={function() { setApplied({ type: opt.value }); }}
                                                    style={{ flex: 1, padding: '8px 14px', borderRadius: 20, cursor: 'pointer', textAlign: 'center', border: '1.5px solid ' + (isActive ? opt.color : '#e8e8e8'), background: isActive ? opt.color + '10' : '#fff', fontSize: 12, fontWeight: isActive ? 600 : 400, color: isActive ? opt.color : '#595959', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                                    <Icon type={opt.icon} style={{ fontSize: 12 }} />
                                                    {opt.label}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        }>
                            <Badge count={countActiveExtratoFilters(applied, defaults)} offset={[-4, 0]} style={{ backgroundColor: '#722ed1' }}>
                                <Button icon="bars" style={{ borderRadius: 8 }}>Filtros</Button>
                            </Badge>
                        </Popover>
                    }
                    advanced={
                        <FilterPillsBar pills={pills} onClearAll={persisted.isDefault ? undefined : persisted.resetFilters} />
                    }
                />
            </ModuleHeaderWrapper>

            <RestoredFiltersNotice
                visible={persisted.wasRestored}
                summary={restoredSummary}
                onReset={persisted.resetFilters}
                onDismiss={persisted.dismissRestored}
            />

            <div style={{ display: 'flex', gap: 12, padding: '10px 0', flexWrap: 'nowrap' }}>
                <SummaryCard>
                    <div style={{ fontSize: 11, color: '#8c8c8c' }}><Icon type="clock-circle" style={{ marginRight: 4 }} />Receitas em aberto</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#52c41a' }}>R$ {toBRL(summary.receitas_abertas || 0)}</div>
                </SummaryCard>
                <SummaryCard>
                    <div style={{ fontSize: 11, color: '#8c8c8c' }}><Icon type="check-circle" style={{ marginRight: 4 }} />Receitas realizadas</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#52c41a' }}>R$ {toBRL(summary.receitas_realizadas || 0)}</div>
                </SummaryCard>
                <SummaryCard>
                    <div style={{ fontSize: 11, color: '#8c8c8c' }}><Icon type="clock-circle" style={{ marginRight: 4 }} />Despesas em aberto</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#f5222d' }}>R$ {toBRL(summary.despesas_abertas || 0)}</div>
                </SummaryCard>
                <SummaryCard>
                    <div style={{ fontSize: 11, color: '#8c8c8c' }}><Icon type="check-circle" style={{ marginRight: 4 }} />Despesas realizadas</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#f5222d' }}>R$ {toBRL(summary.despesas_realizadas || 0)}</div>
                </SummaryCard>
                <SummaryCard style={{ borderColor: '#1890ff', borderWidth: 2 }}>
                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                        Total do período
                        <Icon type="question-circle" style={{ marginLeft: 4, fontSize: 11, color: '#bfbfbf' }} />
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: summary.total_periodo >= 0 ? '#52c41a' : '#f5222d' }}>
                        {summary.total_periodo < 0 ? '-' : ''}R$ {toBRL(Math.abs(summary.total_periodo || 0))}
                    </div>
                </SummaryCard>
            </div>

            <TableList data={filtered} isLoading={isLoading} summary={summary} filter={filter} />
        </div>
    );
};

export default ExtratoList;
